import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
  Chip,
} from '@mui/material';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

const MultiSelect = ({
  label,
  value = [],
  options = [],
  getOptionValue = (option) => option.value,
  getOptionLabel = (option) => option.label,
  onChange,
  disabled = false,
  loading = false,
  placeholder = 'Select...',
  renderOption,
  labelAction,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedOptions = useMemo(() => {
    return options.filter((option) => value.includes(getOptionValue(option)));
  }, [options, value, getOptionValue]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) {
      return options;
    }

    const searchValue = search.trim().toLowerCase();

    return options.filter((option) =>
      String(getOptionLabel(option)).toLowerCase().includes(searchValue),
    );
  }, [options, search, getOptionLabel]);

  const handleChange = (event) => {
    const selected = event.target.value;

    onChange(typeof selected === 'string' ? selected.split(',') : selected);

    setSearch(''); // clear typed text after selecting from the results
  };

  const handleDelete = (optionValue) => {
    onChange(value.filter((item) => item !== optionValue));
  };

  const handleOpen = () => {
    setSearch('');
    setOpen(true);
  };

  const handleClose = () => {
    setSearch('');
    setOpen(false);
  };

  /*
   * Search directly by typing while
   * the Select dropdown is open.
   */
  const handleMenuKeyDown = (event) => {
    // Ignore special/navigation keys
    if (
      event.key === 'ArrowUp' ||
      event.key === 'ArrowDown' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'Enter' ||
      event.key === 'Escape' ||
      event.key === 'Tab'
    ) {
      return;
    }

    // Backspace
    if (event.key === 'Backspace') {
      event.preventDefault();

      setSearch((prev) => prev.slice(0, -1));

      return;
    }

    // Normal characters
    if (event.key.length === 1) {
      event.preventDefault();

      setSearch((prev) => `${prev}${event.key}`);
    }
  };

  return (
    <FormControl
      fullWidth
      disabled={disabled}
      sx={{
        '& .MuiOutlinedInput-root': {
          minHeight: 46,
          borderRadius: '10px',
          backgroundColor: '#fff',

          '& fieldset': {
            borderColor: '#d8d3c5',
          },

          '&:hover fieldset': {
            borderColor: '#cfa45f',
          },

          '&.Mui-focused fieldset': {
            borderColor: '#cfa45f',
            borderWidth: 1,
          },

          '& .MuiSelect-select': {
            padding: '8px 40px 8px 8px !important',
            display: 'flex',
            alignItems: 'center',
            cursor: open ? 'text' : 'pointer',
          },
        },
      }}
    >
      {/* LABEL */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.7,
        }}
      >
        <InputLabel
          shrink
          sx={{
            position: 'static',
            transform: 'none',
            fontSize: 12,
            fontWeight: 700,
            color: '#817657',
            letterSpacing: 0.5,
            mb: 0,
          }}
        >
          {label}
        </InputLabel>

        {labelAction}
      </Box>

      {/* SELECT */}
      <Select
        multiple
        value={value}
        onChange={handleChange}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        input={<OutlinedInput />}
        displayEmpty
        IconComponent={open ? ExpandLess : ExpandMore}
        renderValue={(selected) => {
          const hasSelection = selectedOptions.length > 0;

          // Placeholder only when closed with nothing selected
          if (!open && !hasSelection) {
            return (
              <Typography color='text.secondary' fontSize={14}>
                {placeholder}
              </Typography>
            );
          }

          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
              {/* Retain selected chips while searching */}
              {selectedOptions.map((option) => {
                const optionValue = getOptionValue(option);

                return (
                  <Chip
                    key={optionValue}
                    label={getOptionLabel(option)}
                    size='small'
                    variant='outlined'
                    sx={{
                      height: 25,
                      borderRadius: '7px',
                      borderColor: '#d7c8a5',
                      backgroundColor: '#faf6eb',
                      fontSize: 14,
                      fontWeight: 600,
                      '& .MuiChip-label': { px: 0.8 },
                      '& .MuiChip-deleteIcon': { fontSize: 15 },
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                    onDelete={() => handleDelete(optionValue)}
                  />
                );
              })}

              {/* Search keyword + blinking text cursor while open */}
              {open && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Typography fontSize={14} sx={{ color: '#333', whiteSpace: 'pre' }}>
                    {search}
                  </Typography>
                  <Box
                    component='span'
                    sx={{
                      width: '1px',
                      height: 18,
                      ml: '1px',
                      backgroundColor: '#333',
                      animation: 'multiSelectCaretBlink 1s step-start infinite',
                      '@keyframes multiSelectCaretBlink': {
                        '50%': { opacity: 0 },
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 0.5,
              borderRadius: '10px',
              border: '1px solid #ded9ca',
              boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
              maxHeight: 350,

              '& .MuiList-root': {
                padding: 0,
              },
            },

            onKeyDown: handleMenuKeyDown,
          },
        }}
      >
        {/* LOADING */}
        {loading ? (
          <Box
            sx={{
              height: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <CircularProgress
              size={20}
              sx={{
                color: '#9a8252',
              }}
            />

            <Typography fontSize={13} color='text.secondary'>
              Loading options...
            </Typography>
          </Box>
        ) : options.length === 0 ? (
          <MenuItem disabled>
            <Typography fontSize={13} color='text.secondary'>
              No options available
            </Typography>
          </MenuItem>
        ) : filteredOptions.length === 0 ? (
          <MenuItem disabled>
            <Typography fontSize={13} color='text.secondary'>
              No matching options found
            </Typography>
          </MenuItem>
        ) : (
          filteredOptions.map((option) => {
            const optionValue = getOptionValue(option);

            const checked = value.includes(optionValue);

            return (
              <MenuItem
                key={optionValue}
                value={optionValue}
                sx={{
                  minHeight: 52,
                  px: 1,
                  py: 0.7,
                  alignItems: 'center',

                  '&.Mui-selected': {
                    backgroundColor: 'rgba(207, 164, 95, 0.08)',
                  },

                  '&.Mui-selected:hover': {
                    backgroundColor: 'rgba(207, 164, 95, 0.14)',
                  },
                }}
              >
                <Checkbox
                  checked={checked}
                  size='small'
                  sx={{
                    p: 0.5,
                    mr: 0.8,

                    '&.Mui-checked': {
                      color: '#9a8252',
                    },
                  }}
                />

                {renderOption ? (
                  renderOption(option)
                ) : (
                  <Typography fontSize={13} fontWeight={600}>
                    {getOptionLabel(option)}
                  </Typography>
                )}
              </MenuItem>
            );
          })
        )}
      </Select>
    </FormControl>
  );
};

MultiSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.array,
  loading: PropTypes.bool,
  options: PropTypes.array,
  getOptionValue: PropTypes.func,
  getOptionLabel: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  renderOption: PropTypes.func,
  labelAction: PropTypes.node,
};

export default MultiSelect;
